/**
 * FieldSuggestInput
 * A text input with an autocomplete dropdown backed by prior submission values.
 * Degrades to a plain text input when formSchemaId is not provided.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import formSubmissionService from '../api/services/formSubmissionService';

const Wrapper = styled.div`
  position: relative;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const LabelEl = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: ${props => props.theme.typography.weights.semiBold};
`;

const StyledInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$dynamicBg || props.theme.colors.background};
  color: ${props => props.$dynamicFg || props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  min-height: 40px;
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.tint}20;
  }

  &::placeholder {
    color: ${props => props.$dynamicFg || props.theme.colors.muted};
    ${props => props.$dynamicFg && 'opacity: 0.65;'}
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-top: none;
  border-radius: 0 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md};
  z-index: 1000;
  box-shadow: ${props => props.theme.shadows.md};
`;

const SuggestionItem = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  transition: background-color 0.15s ease;
  background-color: ${props =>
    props.$highlighted
      ? props.theme.colors.tint + '20'
      : 'transparent'};

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
  }
`;

const LoadingItem = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

/**
 * @param {Object} props
 * @param {string} [props.label]
 * @param {string} [props.placeholder]
 * @param {string} props.value
 * @param {function(string): void} props.onChange - receives string value directly (not a DOM event)
 * @param {boolean} [props.required]
 * @param {number} [props.formSchemaId] - when undefined, degrades to plain text input
 * @param {string} [props.fieldKey]
 */
export function FieldSuggestInput({
  label,
  placeholder,
  value,
  onChange,
  required,
  formSchemaId,
  fieldKey,
  dynamicColor,
}) {
  const theme = useTheme();
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const skipNextFetchRef = useRef(false);

  const fetchSuggestions = useCallback(async (text) => {
    if (!formSchemaId || !fieldKey || !text || text.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    setLoading(true);
    try {
      const results = await formSubmissionService.getFieldSuggestions(
        formSchemaId,
        fieldKey,
        text,
        10
      );
      const list = Array.isArray(results) ? results : [];
      setSuggestions(list);
      setIsOpen(list.length > 0);
    } catch {
      // Suggestions are non-critical — fail silently
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  }, [formSchemaId, fieldKey]);

  // Debounce fetch on value changes.
  // Skip if the value change was caused by the user selecting a suggestion (not typing).
  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, fetchSuggestions]);

  const handleSelect = (suggestion) => {
    // Set flag before onChange so the value-change useEffect skips its fetch
    skipNextFetchRef.current = true;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onChange(suggestion);
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setHighlightedIndex(-1);
  };

  const handleBlur = () => {
    // Delay to allow onMouseDown on items to fire first
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(i => Math.min(i + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <Wrapper theme={theme}>
      {label && <LabelEl theme={theme}>{label}</LabelEl>}
      <StyledInput
        ref={inputRef}
        theme={theme}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        required={required}
        $dynamicBg={dynamicColor?.bg}
        $dynamicFg={dynamicColor?.fg}
      />
      {isOpen && (
        <Dropdown theme={theme}>
          {loading ? (
            <LoadingItem theme={theme}>Loading...</LoadingItem>
          ) : (
            suggestions.map((s, idx) => (
              <SuggestionItem
                key={s}
                theme={theme}
                $highlighted={idx === highlightedIndex}
                onMouseDown={() => handleSelect(s)}
              >
                {s}
              </SuggestionItem>
            ))
          )}
        </Dropdown>
      )}
    </Wrapper>
  );
}

export default FieldSuggestInput;
